// カートの最初のページ（/cart、#shoppingcart_page_step0）に「見積書発行」ボタンを足す。
// 押すとカートの上にダイアログを出して宛名・支払い方法・送付先を聞き、発行でカートの中身と一緒に
// wuStorefront の /api/cart-estimate/ へ POST して、印刷用の見積書を別タブで開く（Issue #115）。
// ★店ごとに移す。ダイアログにするのは shared/cart-estimate.ts の ESTIMATE_DIALOG_SITES にある店だけで、
//   ほかの店は旧の流れ（旧 OppSite の /Estimate/Print をクエリ付きで別タブに開く）のまま。
// wuSiteResource2 の各店 SiteCommon.ts の setupEstimateButton() と Shared/SiteCommon.ts の
// toEstimate() / parseProduct() の引き取り先（Issue #53）。
//
// 旧の流れでは、宛名などは旧 OppSite の入力の画面で聞いている。
// ダイアログの選択肢（店ごとの支払い方法・送付先・NP の上限）は旧の入力の画面（Print.cshtml）の写しで、
// サーバーの検証と同じ表（shared/cart-estimate.ts）を使う。
//
// おちゃのこ「アクセス解析タグ設定」欄にも同じ機能の**旧**実装（#inputimage に input を足す jump()）が
// 直書きされていたが、#inputimage は現行テンプレートに無く一度も発動していない（4 店で実測）。
// そちらは移さず捨てる。
//
// ★ 過渡期に旧実装（wuSiteResource2）と同居してもボタンが 2 個にならないよう、旧側より後に走らせ
//   （shared/after-old-scripts.ts）、既に #estimate があれば何もしない。
// ★ 発行はフォームの POST（画面の移動）なので CORS は要らない。発行ボタンを押した操作の中で送るので、
//   別タブがポップアップとして止められることもない。
// ★ 構造の CSS（重ね方・並び・入力欄の体裁）はここが持つ。店のテーマに任せない（#46 の 2026-09-17 の事故）。
//   セレクタは単一クラスだけ・クラス名は wu-estimate-*。

import { afterOldScripts } from '../shared/after-old-scripts'
import {
  ESTIMATE_DIALOG_SITES,
  ESTIMATE_SITES,
  NAME_MAX,
  NP_LIMIT_OVER,
  NP_PAY_TYPE,
  NP_WARNING,
  PAY_TYPES,
  PREF_LABELS,
  PREFS,
  asksPref,
  payTypesFor,
  type TargetSite,
} from '../shared/cart-estimate'
import { resolveDevApiOverride } from '../shared/dev-api-override'
import { injectStyleOnce } from '../shared/style'

export const ESTIMATE_URL = 'https://front.workup-system.com/api/cart-estimate/'
/** 旧の流れ（ESTIMATE_DIALOG_SITES に無い店）の飛び先 */
export const OLD_ESTIMATE_URL = 'https://www.workup-system.com/Estimate/Print/'
export const ESTIMATE_BUTTON_ID = 'estimate'

export interface CartItem {
  productName: string
  /** 「1,234円」→ "1234"。数値化はしない（旧実装どおり文字列のまま渡す） */
  unitPrice: string
  quantity: string
}

/** POST の本文に載せる品物。旧 parseProduct() のクエリと同じ名前（`[i].productName` / `[i].unitPrice` / `[i].selectNum`） */
export function estimateFields(siteId: number, items: CartItem[]): [string, string][] {
  const out: [string, string][] = [['siteId', String(siteId)]]
  items.forEach((it, idx) => {
    const q = `[${idx}].`
    out.push([`${q}productName`, it.productName], [`${q}unitPrice`, it.unitPrice], [`${q}selectNum`, it.quantity])
  })
  return out
}

/** 旧 parseProduct() と同じ形。`[i].productName=...&[i].unitPrice=...&[i].selectNum=...`（旧の流れ） */
export function estimateQuery(items: CartItem[]): string {
  return items
    .map((it, idx) => {
      const q = `[${idx}].`
      return `${q}productName=${encodeURIComponent(it.productName)}&${q}unitPrice=${it.unitPrice}&${q}selectNum=${it.quantity}`
    })
    .join('&')
}

/** 旧の流れで開く URL */
export function estimateUrl(siteId: number, items: CartItem[]): string {
  return `${OLD_ESTIMATE_URL}?siteId=${siteId}&${estimateQuery(items)}`
}

/**
 * ダイアログにするか。ESTIMATE_DIALOG_SITES にある店か、ローカル検証の `?wu_estimate_api=`（localhost のときだけ）が付いているとき。
 * 後者は、一覧に足す前の店でダイアログを手元のサーバーに向けて確かめるためのもの
 */
export function usesDialog(siteId: number, search: string): boolean {
  if (!ESTIMATE_SITES[siteId]) return false
  if (ESTIMATE_DIALOG_SITES.includes(siteId)) return true
  return resolveDevApiOverride(search, 'wu_estimate_api', ESTIMATE_URL) !== ESTIMATE_URL
}

export function priceText(raw: string): string {
  return raw.replace('円', '').split(',').join('')
}

/** 旧の `ViewBag.Ovre50000`。カートの品物だけの合計（送料・手数料なし）が 49,999 円を超えるか。サーバーは見ない（旧も画面だけ） */
export function isOverNpLimit(items: CartItem[]): boolean {
  const total = items.reduce((s, it) => s + (Number(it.unitPrice) || 0) * (Number(it.quantity) || 0), 0)
  return total > NP_LIMIT_OVER
}

/** コンビニ後払いを選んでいて上限を超えていれば、発行を止める */
export const blocksIssue = (overNpLimit: boolean, payType: string): boolean => overNpLimit && payType === String(NP_PAY_TYPE)

export function collectCartItems(root: ParentNode): CartItem[] {
  return Array.from(root.querySelectorAll('.cart_data_box')).map((box) => ({
    productName: box.querySelector('.goods_name')?.textContent ?? '',
    unitPrice: priceText(box.querySelector('.cart_price .price')?.textContent ?? ''),
    quantity: box.querySelector<HTMLSelectElement | HTMLInputElement>('.cart_quantity')?.value ?? '',
  }))
}

function buildButton(): HTMLElement {
  const span = document.createElement('span')
  span.className = 'global_btn registerinput_btn'
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.id = ESTIMATE_BUTTON_ID
  btn.className = 'registerinput btn_size_xxlarge btn_color_emphasis'
  for (const [cls, text] of [
    ['button_head_space', ''],
    ['button_text', '見積書発行'],
    ['button_end_space', ''],
  ]) {
    const s = document.createElement('span')
    s.className = cls
    s.textContent = text
    btn.appendChild(s)
  }
  span.appendChild(btn)
  return span
}

const CSS = `
.wu-estimate-overlay { position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 2147483000; display: flex; align-items: center; justify-content: center; padding: 16px; background: rgba(0,0,0,.45); box-sizing: border-box; }
.wu-estimate-dialog { box-sizing: border-box; width: 100%; max-width: 480px; max-height: 100%; overflow-y: auto; margin: 0; padding: 20px 24px; background: #fff; color: #333; border-radius: 6px; box-shadow: 0 6px 24px rgba(0,0,0,.3); font-size: 14px; line-height: 1.6; text-align: left; }
.wu-estimate-title { margin: 0 0 16px; padding: 0; font-size: 20px; font-weight: 700; line-height: 1.3; border: 0; background: none; }
.wu-estimate-field { display: block; margin: 0 0 14px; padding: 0; border: 0; }
.wu-estimate-label { display: block; margin: 0 0 4px; padding: 0; font-weight: 700; font-size: 14px; }
.wu-estimate-input { display: block; box-sizing: border-box; width: 100%; height: 38px; margin: 0; padding: 6px 10px; font-size: 16px; color: #333; background: #fff; border: 1px solid #bbb; border-radius: 4px; }
.wu-estimate-radios { display: flex; flex-wrap: wrap; gap: 4px 18px; }
.wu-estimate-radio { display: inline-flex; align-items: center; gap: 4px; margin: 0; font-weight: 400; cursor: pointer; }
.wu-estimate-note { margin: 0 0 14px; font-size: 13px; }
.wu-estimate-warning { margin: 0 0 14px; color: #d00; font-size: 13px; }
.wu-estimate-buttons { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 10px; }
.wu-estimate-button { box-sizing: border-box; min-width: 120px; margin: 0; padding: 9px 16px; font-size: 15px; line-height: 1.3; border: 1px solid #bbb; border-radius: 4px; background: #fff; color: #333; cursor: pointer; }
.wu-estimate-primary { background: #428bca; border-color: #357ebd; color: #fff; }
.wu-estimate-button:disabled { opacity: .5; cursor: not-allowed; }
`

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag)
  if (className) e.className = className
  if (text !== undefined) e.textContent = text
  return e
}

function hidden(name: string, value: string): HTMLInputElement {
  const i = document.createElement('input')
  i.type = 'hidden'
  i.name = name
  i.value = value
  return i
}

/** ダイアログを開く。発行でフォームを別タブへ POST して閉じる。閉じたらボタンへフォーカスを戻す */
export function openEstimateDialog(siteId: number, site: TargetSite, items: CartItem[], action: string, opener: HTMLElement | null): void {
  injectStyleOnce('cart-estimate', CSS)
  const overlay = el('div', 'wu-estimate-overlay')
  const form = el('form', 'wu-estimate-dialog')
  form.method = 'post'
  form.action = action
  form.target = '_blank'
  form.setAttribute('role', 'dialog')
  form.setAttribute('aria-modal', 'true')
  form.setAttribute('aria-labelledby', 'wu-estimate-title')

  const title = el('h2', 'wu-estimate-title', 'お見積書発行')
  title.id = 'wu-estimate-title'
  form.appendChild(title)

  // 名前
  const nameField = el('div', 'wu-estimate-field')
  const nameLabel = el('label', 'wu-estimate-label', '名前')
  nameLabel.htmlFor = 'wu-estimate-name'
  const name = el('input', 'wu-estimate-input')
  name.type = 'text'
  name.id = 'wu-estimate-name'
  name.name = 'name'
  name.maxLength = NAME_MAX
  name.placeholder = '名前を入力してください'
  name.autocomplete = 'name'
  nameField.append(nameLabel, name)
  form.appendChild(nameField)

  // 支払い方法（旧と同じく「選択なし」から。店ごとの出し分けは shared/cart-estimate.ts）
  const payField = el('div', 'wu-estimate-field')
  const payLabel = el('label', 'wu-estimate-label', '支払い方法')
  payLabel.htmlFor = 'wu-estimate-pay'
  const pay = el('select', 'wu-estimate-input')
  pay.id = 'wu-estimate-pay'
  pay.name = 'payType'
  pay.appendChild(new Option('選択なし', ''))
  const pays = payTypesFor(site)
  for (const p of PAY_TYPES) if (pays.includes(p.value)) pay.appendChild(new Option(p.label, String(p.value)))
  payField.append(payLabel, pay)
  form.appendChild(payField)

  // 送付先
  if (asksPref(site)) {
    const prefField = el('fieldset', 'wu-estimate-field')
    prefField.appendChild(el('legend', 'wu-estimate-label', '送付先選択'))
    const radios = el('div', 'wu-estimate-radios')
    PREFS.forEach((p, i) => {
      const label = el('label', 'wu-estimate-radio')
      const r = document.createElement('input')
      r.type = 'radio'
      r.name = 'pref'
      r.value = p
      r.checked = i === 0
      label.append(r, document.createTextNode(PREF_LABELS[p]))
      radios.appendChild(label)
    })
    prefField.appendChild(radios)
    form.appendChild(prefField)
  } else {
    form.appendChild(hidden('pref', 'その他'))
  }

  form.appendChild(el('p', 'wu-estimate-note', 'お名前を入力して、見積書発行ボタンを押すとカート内容の見積書が作成されます。'))
  const warning = el('p', 'wu-estimate-warning', NP_WARNING)
  warning.hidden = true
  form.appendChild(warning)

  for (const [k, v] of estimateFields(siteId, items)) form.appendChild(hidden(k, v))

  const buttons = el('div', 'wu-estimate-buttons')
  const cancel = el('button', 'wu-estimate-button', '閉じる')
  cancel.type = 'button'
  const submit = el('button', 'wu-estimate-button wu-estimate-primary', '見積書発行')
  submit.type = 'submit'
  buttons.append(cancel, submit)
  form.appendChild(buttons)

  overlay.appendChild(form)

  const over = isOverNpLimit(items)
  const sync = () => {
    const blocked = blocksIssue(over, pay.value)
    submit.disabled = blocked
    warning.hidden = !blocked
  }
  pay.addEventListener('change', sync)
  sync()

  // フォーカスはダイアログの中だけを回す（Tab / Shift+Tab の端で反対の端へ。後ろのカートへ抜けると、見えないまま
  // カートを操作したり、見積書ボタンでダイアログを重ねて開けたりする）。Codex のレビュー（PR #116）
  const focusables = () =>
    Array.from(form.querySelectorAll<HTMLElement>('input:not([type="hidden"]):not([disabled]), select:not([disabled]), button:not([disabled])'))
  const close = () => {
    document.removeEventListener('keydown', onKey, true)
    document.removeEventListener('focusin', onFocusIn, true)
    overlay.remove()
    opener?.focus()
  }
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
      return
    }
    if (e.key !== 'Tab') return
    const list = focusables()
    const firstEl = list[0]
    const lastEl = list[list.length - 1]
    if (!firstEl || !lastEl) return
    const active = document.activeElement
    if (e.shiftKey && (active === firstEl || !form.contains(active))) {
      e.preventDefault()
      lastEl.focus()
    } else if (!e.shiftKey && (active === lastEl || !form.contains(active))) {
      e.preventDefault()
      firstEl.focus()
    }
  }
  // Tab 以外（店の JS がフォーカスを動かすなど）で外へ出たときも中へ戻す
  const onFocusIn = (e: FocusEvent) => {
    if (e.target instanceof Node && !overlay.contains(e.target)) name.focus()
  }
  document.addEventListener('keydown', onKey, true)
  document.addEventListener('focusin', onFocusIn, true)
  cancel.addEventListener('click', close)
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close()
  })
  form.addEventListener('submit', (e) => {
    if (blocksIssue(over, pay.value)) {
      e.preventDefault()
      return
    }
    // 送信が始まる前にフォームを外すと送られないので、閉じるのは次の番にする
    submit.disabled = true
    setTimeout(close, 0)
  })

  document.body.appendChild(overlay)
  name.focus()
}

export default function cartEstimate(): void {
  const siteId = window.wu?.siteInfoId
  if (!siteId) return
  const site = ESTIMATE_SITES[siteId]
  const dialog = site !== undefined && usesDialog(siteId, location.search)
  const action = resolveDevApiOverride(location.search, 'wu_estimate_api', ESTIMATE_URL)
  afterOldScripts(() => {
    if (document.getElementById(ESTIMATE_BUTTON_ID)) return // 旧実装が先に出している
    const formBtns = document.querySelectorAll('#register .form_btn')
    const last = formBtns[formBtns.length - 1]
    if (!last) return
    const span = buildButton()
    last.appendChild(span)
    const btn = span.querySelector('button')
    btn?.addEventListener('click', () => {
      if (dialog && site) openEstimateDialog(siteId, site, collectCartItems(document), action, btn)
      else window.open(estimateUrl(siteId, collectCartItems(document)))
    })
  })
}
